import CancelIcon from "@mui/icons-material/Cancel";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled, Theme, useMediaQuery } from "@mui/material";
import {
  createMRTColumnHelper,
  type MRT_TableInstance,
  type MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
} from "material-react-table";
import {
  searchFieldSpacing,
  searchTextFieldProps,
  topToolbarFitsThePage,
} from "_shared/_views/tables/table-toolbar";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import RowActions from "_shared/_views/tables/row-actions";
import UserEntityMemberInvitation, {
  MemberInvitationNotOpenError,
} from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import {
  useCancelUserEntityMemberInvitationMutation,
  useGetActiveUserEntityMemberInvitationsQuery,
} from "legal-entities/invitations/member-invitations/email-based/_api/user-entity-member-invitation-api-service";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import { enqueueSnackbar } from "notistack";
import { ToolbarButtons, ToolbarButtonsContainer } from "permissions/_views/tables/permissions-table-styles";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";

type EntityMemberInvitationsTableProps = {
  canCancelInvitations: boolean;
  canInviteMembers: boolean;
  renderTopToolbarCustomActions: (props: { table: MRT_TableInstance<UserEntityMemberInvitation> }) => ReactNode;
};

const invitationColumnHelper = createMRTColumnHelper<UserEntityMemberInvitation>();

const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1.5),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

export default function EntityMemberInvitationsTable(props: Readonly<EntityMemberInvitationsTableProps>) {
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const { canCancelInvitations, canInviteMembers, renderTopToolbarCustomActions } = props;
  const navigate = useNavigate();
  const session = useSession();
  const confirm = useConfirmDialog();
  const [cancelUserEntityMemberInvitation] = useCancelUserEntityMemberInvitationMutation();
  const [cancelingInvitationId, setCancelingInvitationId] = useState<string | null>(null);
  const { data: invitations, error, isFetching, isLoading, refetch } = useGetActiveUserEntityMemberInvitationsQuery({
    entityId: session.user!.legalEntityId,
  });

  const cancelInvitation = useCallback(
    async (invitation: UserEntityMemberInvitation) => {
      if (!invitation.id || invitation.status !== InvitationStatus.Open) return;

      const response = await confirm({
        title: "Cancel invitation?",
        message: `Cancel the invitation for ${invitation.registrationInfo.email.value}?`,
        okButtonText: "Cancel invitation",
        cancelButtonText: "Keep invitation",
      });
      if (response === ConfirmResponse.Cancel) return;

      try {
        setCancelingInvitationId(invitation.id.value);
        await cancelUserEntityMemberInvitation({ invitation }).unwrap();
        enqueueSnackbar("Invitation canceled", { variant: "success" });
      } catch (cancelError) {
        console.error(cancelError);
        enqueueSnackbar(
          cancelError instanceof MemberInvitationNotOpenError
            ? cancelError.message
            : "Failed to cancel invitation",
          { variant: "error" },
        );
      } finally {
        setCancelingInvitationId(null);
      }
    },
    [cancelUserEntityMemberInvitation, confirm],
  );

  const renderNameColumn = useCallback((invitation: UserEntityMemberInvitation) => (
    <NameCellContainer>
      <IndividualAvatar />
      <RowTitle>
        {invitation.registrationInfo.name.firstName} {invitation.registrationInfo.name.lastName}
      </RowTitle>
    </NameCellContainer>
  ), []);

  const columns = useMemo(
    () => [
      invitationColumnHelper.accessor(
        (invitation) => `${invitation.registrationInfo.name.firstName} ${invitation.registrationInfo.name.lastName}`,
        {
          header: "Name",
          id: "name",
          Cell: ({ row }) => renderNameColumn(row.original),
        },
      ),
      invitationColumnHelper.accessor((invitation) => invitation.registrationInfo.email.value, {
        header: "Email address",
        id: "email",
      }),
      invitationColumnHelper.accessor((invitation) => invitation.status, { header: "Status", id: "status" }),
      invitationColumnHelper.accessor((invitation) => invitation.expires?.format("MM/DD/YY") ?? "—", {
        header: "Expires on",
        id: "expires",
      }),
    ],
    [renderNameColumn],
  );

  const renderToolbarInternalActions = useCallback(
    (toolbarProps: { table: MRT_TableInstance<UserEntityMemberInvitation> }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={toolbarProps.table} sx={searchFieldSpacing} />
              <ToggleFiltersButton table={toolbarProps.table} />
              <ShowHideColumnsButton table={toolbarProps.table} />
              <ToggleFullScreenButton table={toolbarProps.table} />
            </ToolbarButtons>
            {canInviteMembers && (
              <Button startIcon={<PersonAddIcon />} onClick={() => navigate("/organization/invitations/invite")}>
                Invite member
              </Button>
            )}
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    [canInviteMembers, isMediumDisplaySize, navigate],
  );
  const options: MRT_TableOptions<UserEntityMemberInvitation> = {
    columns,
    data: invitations ?? [],
    enableColumnFilters: true,
    enableRowActions: canCancelInvitations,
    initialState: { showGlobalFilter: true },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) =>
      searchTextFieldProps({ searches: "invitations", table }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: Boolean(error),
      isLoading: isFetching,
      onRetry: () => void refetch(),
      subject: "invitations",
    }),
    enableRowSelection: false,
    positionActionsColumn: "last",
    enableStickyFooter: true,
    enableStickyHeader: true,
    getRowId: (invitation, index) => invitation.id?.value ?? `skeleton-${index}`,
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTableContainerProps: {
      sx: { flex: 1 },
    },
    muiTablePaperProps: {
      sx: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: error
      ? {
          children: "Error loading invitations",
          color: "error",
        }
      : undefined,
    renderRowActions: canCancelInvitations ? ({ row }) => {
      const invitation = row.original;
      const isCanceling = cancelingInvitationId === invitation.id?.value;
      const isCancelable = invitation.status === InvitationStatus.Open && Boolean(invitation.id);
      return (
        <RowActions
          ariaLabel={`Actions for the invitation to ${invitation.registrationInfo.email.value}`}
          actions={[
            {
              key: "cancel",
              label: isCanceling ? "Canceling..." : "Cancel Invitation",
              ariaLabel: `Cancel the invitation to ${invitation.registrationInfo.email.value}`,
              icon: <CancelIcon />,
              destructive: true,
              onClick: () => void cancelInvitation(invitation),
              disabled: !isCancelable || isCanceling,
              disabledReason: isCanceling ? "Canceling..." : "Only an open invitation can be canceled",
            },
          ]}
        />
      );
    } : undefined,
    renderToolbarInternalActions,
    renderTopToolbarCustomActions,
    rowCount: invitations?.length ?? 0,
    state: {
      isLoading,
      showAlertBanner: Boolean(error),
      showProgressBars: isFetching,
    },
  };

  return <MRTNoMemo<UserEntityMemberInvitation> options={options} />;
}
